import type { PoiIconName } from './poiIcons.js';

// Every leaf names a bundled icon, so a mistyped or removed one is a build
// error instead of a silently missing icon. Structurally a `Node`, which is
// what the generic tag resolver takes.
interface IconNode {
  [key: string]: IconNode | PoiIconName;
}

// hack to have only single icon for churches...
const buildingMapping: IconNode = {
  '*': 'building',
  hut: 'hut',
  church: 'church',
  chapel: 'church',
  farm: 'farm',
  parking: 'parking',
  warehouse: 'warehouse',
};

// `power=generator` and `power=plant` classify their source the same way, under
// different tag keys — a plant being the site, a generator a single unit.
const powerSourceMapping: IconNode = {
  biofuel: 'temaki:plant',
  biogas: 'temaki:plant',
  biomass: 'temaki:plant',
  coal: 'industry',
  diesel: 'fuel',
  gas: 'temaki:gas',
  gasoline: 'fuel',
  geothermal: 'temaki:geyser_from_ground',
  hydro: 'dam',
  nuclear: 'temaki:radiation',
  oil: 'temaki:oil_well',
  solar: 'solar_panel',
  waste: 'temaki:waste',
  wind: 'temaki:wind_turbine',
};

export const osmTagToIconMapping: IconNode = {
  aerialway: {
    '*': 'aerialway',
  },
  aeroway: {
    aerodrome: 'aerodrome',
    helipad: 'heliport',
    terminal: 'terminal',
    apron: 'temaki:planes',
    gate: 'gate',
    hangar: 'temaki:hangar',
    holding_position: 'temaki:plane_taxiing',
    jet_bridge: 'temaki:pedestrian_walled',
    parking_position: 'temaki:plane_taxiing',
    taxiway: 'temaki:plane_taxiing',
    windsock: 'temaki:windsock',
  },
  amenity: {
    animal_shelter: 'animal_shelter',
    atm: 'atm',
    arts_centre: 'arts_centre',
    bank: 'bank',
    bar: 'bar',
    bbq: 'bbq',
    bench: 'bench',
    bicycle_parking: 'bicycle_parking',
    bicycle_repair_station: 'bicycle_repair_station',
    bicycle_rental: 'rental_bicycle',
    biergarten: 'biergarten',
    boat_rental: 'boat_rental',
    bureau_de_change: 'bureau_de_change',
    bus_station: 'bus_station',
    cafe: 'cafe',
    car_rental: 'rental_car',
    car_wash: 'car_wash',
    casino: 'casino',
    charging_station: 'charging_station',
    cinema: 'cinema',
    clinic: 'doctors',
    college: 'college',
    community_centre: 'community_centre',
    courthouse: 'courthouse',
    dentist: 'dentist',
    doctors: 'doctors',
    drinking_water: 'drinking_water',
    embassy: 'embassy',
    emergency_phone: 'emergency_phone',
    excrement_bags: 'excrement_bags',
    fast_food: 'fast_food',
    feeding_place: 'manger',
    game_feeding: 'manger',
    ferry: 'ferry',
    fire_station: 'fire_station',
    fountain: 'fountain',
    fuel: 'fuel',
    grave_yard: 'cemetery',
    hospital: 'hospital',
    hunting_stand: 'hunting_stand',
    ice_cream: 'ice_cream',
    internet_cafe: 'internet_cafe',
    library: 'library',
    motorcycle_parking: 'motorcycle_parking',
    nightclub: 'nightclub',
    parking: 'parking',
    parking_entrance: 'parking',
    parking_space: 'parking',
    pharmacy: 'pharmacy',
    place_of_worship: {
      '*': 'place_of_worship',
      building: buildingMapping,
    },
    police: 'police',
    post_box: 'post_box',
    post_office: 'post_office',
    prison: 'prison',
    pub: 'pub',
    public_bath: 'public_bath',
    public_bookcase: 'public_bookcase',
    ranger_station: 'ranger-station',
    recycling: 'recycling',
    restaurant: 'restaurant',
    school: 'school',
    shelter: {
      '*': 'shelter',
      shelter_type: {
        basic_hut: 'basic_hut',
        lean_to: 'lean_to',
        picnic_shelter: 'picnic_shelter',
        public_transport: 'public_transport',
        weather_shelter: 'weather_shelter',
      },
    },
    shower: 'shower',
    ski_rental: 'skiing_downhill',
    social_facility: 'social_facility',
    taxi: 'taxi',
    telephone: 'telephone',
    theatre: 'theatre',
    toilets: 'toilets',
    townhall: 'townhall',
    university: 'university',
    vehicle_inspection: 'vehicle_inspection',
    vending_machine: {
      '*': 'vending_machine',
      vending: {
        parking_tickets: 'parking_tickets',
        public_transport_tickets: 'public_transport_tickets',
      },
    },
    veterinary: 'veterinary',
    waste_basket: 'waste_basket',
    waste_disposal: 'waste_disposal',
    watering_place: 'watering_place',
    animal_breeding: 'veterinary',
    clock: 'temaki:clock',
    device_charging_station: 'temaki:electronic',
    dressing_room: 'maki:clothing-store',
    driving_school: 'temaki:car_pool',
    ferry_terminal: 'ferry',
    funeral_hall: 'cemetery',
    kindergarten: 'school',
    language_school: 'school',
    marketplace: 'marketplace',
    monastery: 'place_of_worship',
    money_transfer: 'temaki:money_hand',
    motorcycle_rental: 'temaki:motorcycle_rental',
    parcel_locker: 'temaki:vending_lockers',
    planetarium: 'maki:globe',
    traffic_park: 'bicycle',
    vacuum_cleaner: 'temaki:vacuum_station',
    water_point: 'drinking_water',
  },
  barrier: {
    cycle_barrier: 'cycle_barrier',
    entrance: 'entrance',
    fence: 'fence',
    gate: 'gate',
    kissing_gate: 'kissing_gate',
    lift_gate: 'lift_gate',
    motorcycle_barrier: 'motorcycle_barrier',
    stile: 'stile',
    swing_gate: 'lift_gate',
    toll_booth: 'toll_booth',
    cattle_grid: 'cattle_grid',
    bollard: 'temaki:bollard',
    border_control: 'temaki:passport_checkpoint',
    cable_barrier: 'maki:roadblock',
    chain: 'temaki:rope_fence',
    city_wall: 'temaki:wall',
    ditch: 'temaki:trench',
    guard_rail: 'temaki:guard_rail',
    handrail: 'temaki:railing',
    hedge: 'temaki:hedge',
    kerb: 'temaki:kerb-raised',
    retaining_wall: 'temaki:wall',
    rope: 'temaki:rope_fence',
    turnstile: 'temaki:turnstile',
    wall: 'temaki:wall',
  },
  bridge: {
    '*': 'bridge',
  },
  boundary: {
    administrative: {
      admin_level: {
        6: 'city',
        9: 'village',
      },
    },
  },
  craft: {
    // Anything not named below still reads as a workshop.
    '*': 'temaki:tools',
    bakery: 'bakery',
    beekeeper: 'beehive',
    blacksmith: 'temaki:anvil_and_hammer',
    brewery: 'temaki:storage_fermenter',
    builder: 'construction',
    caterer: 'temaki:catering',
    cleaning: 'temaki:vacuum',
    confectionery: 'maki:confectionery',
    distillery: 'temaki:storage_fermenter',
    dressmaker: 'temaki:dress',
    electrician: 'temaki:power',
    electronics_repair: 'electronics',
    gardener: 'maki:garden-centre',
    glaziery: 'temaki:window',
    handicraft: 'temaki:vase',
    hvac: 'temaki:tools',
    jeweller: 'jewelry',
    locksmith: 'temaki:lock',
    metal_construction: 'temaki:tools',
    painter: 'maki:paint',
    photographer: 'photo',
    plumber: 'temaki:plumber',
    pottery: 'temaki:vase',
    sawmill: 'maki:logging',
    shoemaker: 'temaki:hammer_shoe',
    stonemason: 'temaki:brick_trowel',
    tailor: 'temaki:needle_and_spool',
    tiler: 'temaki:brick_trowel',
    upholsterer: 'furniture',
    watchmaker: 'maki:watch',
    window_construction: 'temaki:window',
    winery: 'maki:alcohol-shop',
  },
  emergency: {
    defibrillator: 'defibrillator',
    phone: 'emergency_phone',
  },
  entrance: {
    '*': 'entrance',
    // no: no_entrance, // TODO
  },
  ford: {
    yes: 'ford',
  },
  highway: {
    bus_stop: 'bus_stop',
    elevator: 'elevator',
    ford: 'ford',
    rest_area: 'highway-rest-area',
    steps: 'stairs',
    traffic_signals: 'traffic_signals',
    bridleway: 'horse_riding',
    construction: 'construction',
    footway: 'temaki:pedestrian',
    raceway: 'temaki:speedway_oval',
    street_lamp: 'temaki:street_lamp_arm',
    // The road classes iD draws in perspective, so a trunk road reads as a
    // wider one than a lane. Link roads take their parent's drawing.
    motorway: 'id:highway-motorway',
    motorway_link: 'id:highway-motorway',
    trunk: 'id:highway-trunk',
    trunk_link: 'id:highway-trunk',
    primary: 'id:highway-primary',
    primary_link: 'id:highway-primary',
    secondary: 'id:highway-secondary',
    // The label mapping spells this one with a typo; both are kept so the icon
    // shows either way.
    secondaty_link: 'id:highway-secondary',
    secondary_link: 'id:highway-secondary',
    tertiary: 'id:highway-tertiary',
    tertiary_link: 'id:highway-tertiary',
    unclassified: 'id:highway-unclassified',
    residential: 'id:highway-residential',
    living_street: 'id:highway-living-street',
    service: 'id:highway-service',
    // Nothing draws a track or a path as such, so they take the plain line.
    path: 'id:other-line',
    road: 'id:other-line',
    track: 'id:other-line',
    platform: 'temaki:board_transit',
    via_ferrata: 'hillclimbing',
  },
  historic: {
    archaeological_site: 'archaeological_site',
    boundary_stone: 'boundary_stone',
    castle: {
      '*': 'castle',
      castle_type: {
        fortress: 'fortress',
        palace: 'palace',
      },
    },
    city_gate: 'city_gate',
    fort: 'fort',
    manor: 'manor',
    ruins: {
      '*': 'ruins',
      ruins: {
        castle: 'castle',
      },
    },
    memorial: {
      '*': 'memorial',
      memorial: {
        bust: 'bust',
        plaque: 'plaque',
        statue: 'statue',
        stone: 'memorial_stone',
      },
    },
    mine: 'mine',
    mine_shaft: 'mine',
    monument: 'monument',
    obelisk: 'obelisk',
    wayside_cross: 'cross',
    wayside_shrine: 'wayside_shrine',
    building: 'building',
    church: 'church',
    stone: 'stone',
    tomb: 'cemetery',
  },
  landuse: {
    garages: 'warehouse',
    cemetery: 'cemetery',
    commercial: 'commercial',
    construction: 'construction',
    farmland: 'farm',
    farmyard: 'farm',
    industrial: {
      '*': 'industry',
      industrial: 'mine',
    },
    logging: 'logging',
    residential: 'residential-community',
    allotments: 'garden_centre',
    basin: 'maki:water',
    brownfield: 'temaki:bulldozer',
    education: 'school',
    forest: 'maki:park-alt1',
    grass: 'temaki:lawn',
    greenfield: 'temaki:bulldozer',
    landfill: 'temaki:bulldozer',
    meadow: 'maki:garden',
    military: 'temaki:military',
    orchard: 'park',
    quarry: 'temaki:pick_hammer',
    railway: 'temaki:train',
    recreation_ground: 'pitch',
    reservoir: 'maki:water',
    retail: 'commercial',
    vineyard: 'temaki:grapes',
  },
  leisure: {
    bandstand: 'music',
    dance: 'music',
    beach_resort: 'beach_resort',
    bird_hide: 'bird_hide',
    bowling_alley: 'bowling_alley',
    dog_park: 'dog_park',
    firepit: 'firepit',
    fishing: 'fishing',
    fitness_centre: 'fitness_centre',
    fitness_station: 'fitness_station',
    horse_riding: 'horse_riding',
    golf_course: 'golf',
    miniature_golf: 'miniature_golf',
    outdoor_seating: 'outdoor_seating',
    park: 'park',
    picnic_table: 'picnic_table',
    pitch: 'pitch',
    playground: 'playground',
    sauna: 'sauna',
    slipway: 'slipway',
    stadium: 'stadium',
    swimming_pool: 'water_park',
    swimming_area: 'swimming_outdoor',
    track: {
      '*': 'racetrack',
      sport: {
        cycling: 'racetrack-cycling',
        equestrian: 'racetrack-horse',
        horse_racing: 'racetrack-horse',
      },
    },
    water_park: 'water_park',
    adult_gaming_centre: 'casino',
    amusement_arcade: 'maki:gaming',
    bathing_place: 'maki:water',
    bleachers: 'temaki:bleachers',
    disc_golf_course: 'temaki:disc_golf_basket',
    garden: 'maki:garden',
    marina: 'temaki:sailboat',
    nature_reserve: 'park',
    resort: 'maki:lodging',
    sports_centre: 'pitch',
    sports_hall: 'pitch',
  },
  man_made: {
    adit: 'mine',
    antenna: 'antenna',
    apiary: 'beehive',
    beehive: 'beehive',
    bridge: 'bridge',
    chimney: 'chimney',
    cross: 'cross',
    clearcut: 'logging',
    dyke: 'dyke',
    embankment: 'embankment',
    foresters_lodge: "forester's_lodge",
    lighthouse: 'lighthouse',
    mast: 'mast_other',
    mineshaft: 'mine',
    pipeline: 'id:pipeline-line',
    reservoir_covered: 'water_works',
    tower: {
      '*': 'tower_other',
      'tower:type': {
        '*': 'tower_other',
        bell_tower: 'bell_tower',
        communication: 'tower_communication',
        observation: 'tower_observation',
      },
    },
    water_tower: 'water_tower',
    water_well: 'water_well',
    water_works: 'water_works',
    watermill: 'watermill',
    windmill: 'windmill',
    beacon: 'maki:communications-tower',
    bunker_silo: 'temaki:bunker_silo',
    cairn: 'temaki:cairn',
    cutline: 'logging',
    flagpole: 'embassy',
    "forester's_lodge": "forester's_lodge",
    gasometer: 'temaki:storage_tank',
    goods_conveyor: 'temaki:conveyor',
    manhole: 'temaki:manhole',
    monitoring_station: 'antenna',
    observatory: 'temaki:telescope',
    pier: 'temaki:pier_fixed',
    pumping_station: 'temaki:powered_pump',
    silo: 'temaki:silo',
    storage_tank: 'temaki:storage_tank',
    surveillance: 'temaki:security_camera',
    survey_point: 'temaki:benchmark_disk',
    utility_pole: 'temaki:utility_pole',
    wastewater_plant: 'temaki:waste',
    water_tap: 'temaki:water_tap',
    works: 'industry',
  },
  military: {
    '*': 'temaki:military',
    bunker: 'bunker',
    danger_area: 'danger',
    checkpoint: 'temaki:military_checkpoint',
    office: 'temaki:military',
    school: 'school',
    trench: 'temaki:trench',
  },
  natural: {
    arch: 'arch',
    beach: 'beach',
    cave_entrance: 'cave_entrance',
    earth_bank: 'earth_bank',
    gully: 'gully',
    hot_spring: 'hot_spring',
    peak: 'peak',
    rock: 'rock',
    saddle: 'saddle',
    sinkhole: 'sinkhole',
    spring: {
      '*': 'spring',
      drinking_water: {
        no: 'not_drinking_spring',
        yes: 'drinking_spring',
      },
      refitted: {
        yes: {
          '*': 'refitted_spring',
          drinking_water: {
            no: 'refitted_not_drinking_spring',
            yes: 'refitted_drinking_spring',
          },
        },
      },
    },
    stone: 'stone',
    tree: {
      '*': 'temaki:tree_broadleaved',
      denotation: {
        natural_monument: 'tree',
      },
      protected: {
        yes: 'tree_protected',
      },
    },
    volcano: 'volcano',
    wetland: 'wetland',
    bare_rock: 'temaki:boulder3',
    bay: 'beach',
    cliff: 'temaki:cliff_falling_rocks',
    geyser: 'temaki:geyser_from_ground',
    glacier: 'temaki:snow',
    grassland: 'temaki:grass',
    heath: 'temaki:shrub_low',
    ridge: 'temaki:mountain_range',
    scrub: 'temaki:shrub',
    shrub: 'temaki:shrub',
    tree_row: 'temaki:tree_row',
    valley: 'temaki:valley',
    water: {
      '*': 'maki:water',
      // Only the kinds drawn as something other than a plain body are listed;
      // a lake, pond or reservoir takes the drop above.
      water: {
        canal: 'id:waterway-canal',
        ditch: 'id:waterway-ditch',
        drain: 'id:waterway-ditch',
        fish_pass: 'temaki:fish_ladder',
        river: 'id:waterway-river',
        stream: 'id:waterway-stream',
        wastewater: 'temaki:waste',
      },
    },
    wood: 'maki:park-alt1',
  },
  obstacle: {
    '*': 'obstacle',
    vegetation: 'obstacle_vegetation',
    dense_vegetation: 'obstacle_vegetation',
    fallen_tree: 'obstacle_tree',
    fallen_trees: 'obstacle_tree',
    'fallen trees': 'obstacle_tree',
    deadwood: 'obstacle_tree',
    branches: 'obstacle_tree',
    tree: 'obstacle_tree',
    trees: 'obstacle_tree',
    root: 'obstacle_tree',
  },
  place: {
    city: 'city',
    town: 'town',
    farm: 'farm',
    village: 'village',
    hamlet: 'village',
    island: 'temaki:island_trees_building',
    islet: 'temaki:islet_tree',
    isolated_dwelling: 'maki:home',
  },
  railway: {
    '*': 'rail',
    light_rail: 'rail-light',
    subway: 'rail-metro',
  },
  route: {
    bicycle: 'bicycle',
    bus: 'bus_station',
    foot: 'temaki:pedestrian',
    hiking: 'temaki:pedestrian',
    horse: 'horse_riding',
    mtb: 'bicycle',
    piste: 'skiing_downhill',
    railway: 'rail',
    ski: 'skiing_crosscountry',
    tram: 'rail-light',
  },
  ruins: {
    '*': 'ruins',
  },
  shop: {
    '*': 'shop',
    alcohol: 'alcohol',
    art: 'art',
    bag: 'bag',
    bakery: 'bakery',
    beauty: 'beauty',
    bed: 'bed',
    beverages: 'beverages',
    bicycle: 'bicycle',
    bookmaker: 'bookmaker',
    books: 'book',
    butcher: 'butcher',
    car_parts: 'car_parts',
    carpet: 'carpet',
    car_repair: 'car_repair',
    car: 'car',
    charity: 'charity',
    chemist: 'chemist',
    clothes: 'clothes',
    coffee: 'coffee',
    computer: 'computer',
    confectionery: 'confectionery',
    convenience: 'convenience',
    copyshop: 'copyshop',
    dairy: 'dairy',
    deli: 'deli',
    department_store: 'department_store',
    diy: 'diy',
    electronics: 'electronics',
    fabric: 'fabric',
    farm: 'greengrocer',
    florist: 'florist',
    furniture: 'furniture',
    garden_centre: 'garden_centre',
    gift: 'gift',
    greengrocer: 'greengrocer',
    hairdresser: 'hairdresser',
    hardware: 'hardware',
    hifi: 'hifi',
    houseware: 'houseware',
    ice_cream: 'ice_cream',
    interior_decoration: 'interior_decoration',
    jewelry: 'jewelry',
    laundry: 'laundry',
    marketplace: 'marketplace',
    massage: 'massage',
    medical_supply: 'medical_supply',
    mobile_phone: 'mobile_phone',
    musical_instrument: 'musical_instrument',
    music: 'music',
    newsagent: 'newsagent',
    optician: 'optician',
    outdoor: 'outdoor',
    paint: 'paint',
    perfumery: 'perfumery',
    pet: 'pet',
    photo: 'photo',
    seafood: 'seafood',
    second_hand: 'second_hand',
    shoes: 'shoes',
    sports: 'sports',
    stationery: 'stationery',
    supermarket: 'supermarket',
    tea: 'tea',
    ticket: 'ticket',
    tobacco: 'tobacco',
    toys: 'toys',
    trade: 'trade',
    travel_agency: 'travel_agency',
    tyres: 'tyres',
    variety_store: 'variety_store',
    video_games: 'video_games',
    video: 'video',
    watches: 'watch',
  },
  sport: {
    // Most of the sports OSM knows are played on a pitch of some kind and have
    // no icon anywhere; the generic one at least says what sort of place it is.
    '*': 'pitch',
    '8pin': 'bowling_alley',
    '9pin': 'bowling_alley',
    '10pin': 'bowling_alley',
    archery: 'archery',
    american_football: 'american_football',
    baseball: 'baseball',
    basketball: 'basketball',
    beachvolleyball: 'volleyball',
    bicycle: 'bicycle',
    bowling: 'bowling_alley',
    candlepin: 'bowling_alley',
    climbing: 'hillclimbing',
    cricket: 'cricket',
    cycling: 'bicycle',
    diving: 'diving',
    equestrian: 'horse_riding',
    free_flying: 'free_flying',
    golf: 'golf',
    gym: 'gym',
    gymnastics: 'gymnasium',
    horse_racing: 'horse_riding',
    horse_riding: 'horse_riding',
    ice_skating: 'iceskating',
    motor: 'motorracing',
    skateboard: 'skateboard',
    skating: 'iceskating',
    ski_jumping: 'skiing_downhill',
    skiing: 'skiing_downhill',
    soccer: 'soccer',
    swimming: 'swimming_outdoor',
    table_tennis: 'table-tennis',
    tennis: 'tennis',
    volleyball: 'volleyball',
    american_handball: 'temaki:wall',
    athletics: 'pitch',
    australian_football: 'american_football',
    badminton: 'tennis',
    boules: 'pitch',
    bowls: 'pitch',
    climbing_adventure: 'temaki:abseiling',
    field_hockey: 'temaki:field_hockey',
    funnel_ball: 'pitch',
    futsal: 'soccer',
    gaga: 'pitch',
    handball: 'pitch',
    horseshoes: 'temaki:horseshoes',
    karting: 'temaki:speedway_8',
    miniature_golf: 'miniature_golf',
    netball: 'volleyball',
    padel: 'tennis',
    pickleball: 'temaki:pickleball',
    rugby_league: 'american_football',
    rugby_union: 'american_football',
    shuffleboard: 'temaki:shuffleboard',
    softball: 'baseball',
    table_soccer: 'temaki:table_soccer',
    tetherball: 'pitch',
    yoga: 'fitness_centre',
  },
  toll: {
    '*': 'toll',
  },
  tourism: {
    alpine_hut: 'alpine_hut',
    apartment: 'apartment',
    artwork: {
      '*': 'artwork',
      artwork_type: {
        bust: 'bust',
      },
    },
    attraction: 'attraction',
    camp_site: 'camp_site',
    chalet: 'chalet',
    guest_house: 'guest_house',
    hostel: 'hostel',
    hotel: 'hotel',
    information: {
      '*': 'information',
      information: {
        board: 'board',
        guidepost: 'guidepost',
        map: 'map',
        office: 'office',
      },
    },
    motel: 'motel',
    museum: 'museum',
    picnic_site: 'picnic_table',
    viewpoint: 'viewpoint',
    wilderness_hut: 'wilderness_hut',
    zoo: 'zoo',
    aquarium: 'maki:aquarium',
    caravan_site: 'temaki:camper_trailer',
    gallery: 'maki:art-gallery',
    theme_park: 'maki:amusement-park',
  },
  tunnel: {
    '*': 'tunnel',
    // no: '', TODO
  },
  waterway: {
    dam: 'dam',
    waterfall: 'waterfall',
    fish_pass: 'temaki:fish_ladder',
    weir: 'dam',
    canal: 'id:waterway-canal',
    canoe_pass: 'temaki:canoe',
    ditch: 'id:waterway-ditch',
    drain: 'id:waterway-ditch',
    rapids: 'id:waterway-stream',
    river: 'id:waterway-river',
    stream: 'id:waterway-stream',
    tidal_channel: 'id:waterway-stream',
  },
  attraction: {
    amusement_ride: 'maki:amusement-park',
    animal: 'zoo',
    big_wheel: 'maki:amusement-park',
    carousel: 'temaki:amusement_park',
    maze: 'temaki:maze',
    roller_coaster: 'temaki:roller_coaster',
    summer_toboggan: 'temaki:sledding',
    train: 'temaki:train_kids',
  },
  power: {
    generator: {
      '*': 'temaki:power',
      'generator:source': powerSourceMapping,
    },
    plant: {
      '*': 'industry',
      'plant:source': {
        ...powerSourceMapping,
        // A plant is a site, so it gets the cooling towers rather than the
        // trefoil a single reactor unit takes.
        nuclear: 'temaki:cooling_tower_radiation',
      },
    },
    // A line is the run of cable, not the pylon holding it up.
    line: 'id:power-line',
    minor_line: 'id:power-line',
    pole: 'temaki:power_pole',
    substation: 'temaki:power',
    tower: 'temaki:power_tower',
    transformer: 'temaki:power_transformer',
  },
  public_transport: {
    platform: 'temaki:board_transit',
    station: 'temaki:transit',
    stop_position: 'temaki:transit',
  },

  // NOTE keep at the end
  building: buildingMapping,
  fixme: {
    '*': 'fixme',
  },
};
